import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MessageBusService from 'garaje/services/message-bus';
import type ReactRendererService from 'garaje/services/react-renderer';
import type StateService from 'garaje/services/state';
import { hasThreatIntelV2Access } from 'garaje/utils/threat-intel-access';
import urlBuilder from 'garaje/utils/url-builder';
import type { Root } from 'react-dom/client';
import { tracked } from 'tracked-built-ins';

export type LegalTermsEvent = 'emnoShowLegalTerms' | 'closeModal';

export interface LegalTermsMessage {
  event: LegalTermsEvent;
}

export default class CommunicationsAlertsController extends Controller {
  @service declare featureFlags: FeatureFlagsService;
  @service declare messageBus: MessageBusService;
  @service declare reactRenderer: ReactRendererService;
  @service declare state: StateService;

  private root?: Root;

  // EMNO-720: modal state driven by messages the emno-fe threat-dashboard
  // iframe posts. Mirrors the EMNO-485 add-rule pattern in
  // location-overview/communications/settings/controller.ts, minus the
  // context payload (legal-terms content is static) and the showToaster
  // branch (view-only modal, no save/side effect).
  @tracked showLegalTermsModal = false;

  constructor(properties: Record<string, unknown>) {
    super(properties);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  willDestroy(): void {
    super.willDestroy();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.off('embedded-app-message', this, this.handleMessage);
  }

  // True → render the v2 emno-fe iframe. False → render the v1 RRC dashboard
  // (see the {{else}} branch in template.hbs, which fires injectComponent).
  // The route's beforeModel guards access; users only reach here when either
  // v2 access or the v1 flag is on.
  get useMigratedThreatDashboard(): boolean {
    return hasThreatIntelV2Access(this.featureFlags, this.state);
  }

  get iframeUrl(): string {
    const companyId = this.state.currentCompany.id;
    const locationId = this.state.currentLocation.id;

    return urlBuilder.emno.embeddedThreatDashboardUrl(companyId, locationId);
  }

  get legalTermsModalUrl(): string {
    return urlBuilder.emno.embeddedLegalTermsModalUrl(this.state.currentCompany.id);
  }

  /**
   * The iframe bridge. See app/components/embedded-app.gts — every framed
   * iframe rebroadcasts its messages on `embedded-app-message`, and this
   * controller listens for the two scoped to the legal-terms flow:
   *   - `emnoShowLegalTerms` — from the OUTER threat-dashboard iframe: open
   *     the modal.
   *   - `closeModal` — from the MODAL iframe (Done button): shut the modal.
   * All other events on the bus are ignored (belong to other consumers).
   */
  @action
  handleMessage(message: LegalTermsMessage): void {
    if (message.event === 'emnoShowLegalTerms') {
      this.showLegalTermsModal = true;
    } else if (message.event === 'closeModal') {
      this.showLegalTermsModal = false;
    }
  }

  @action
  closeLegalTermsModal(): void {
    this.showLegalTermsModal = false;
  }

  @action
  async injectComponent(): Promise<void> {
    const { ThreatDashboard } = await import('reactRichComponents/communication/ThreatDashboard');

    const locationId = this.state.currentLocation.id;

    this.root = await this.reactRenderer.render(ThreatDashboard, 'react__threat-dashboard', {
      locationId,
      companyId: this.state.currentCompany.id,
    });
  }

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }
}
